import { useEffect, useState } from 'react';
import { useQuery, useMutation } from 'react-query';
import Alert from '@mui/material/Alert';
import Slide from '@mui/material/Slide';
import Snackbar from '@mui/material/Snackbar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import CircleIcon from '@mui/icons-material/Circle';
import CircleOutlinedIcon from '@mui/icons-material/CircleOutlined';
import Grid from '@mui/material/GridLegacy';
import Typography from '@mui/material/Typography';
import { DataGrid } from '@mui/x-data-grid';
import {
	GetPendingSubAccountInfoFn,
	ResendAshlinkUserRegistration,
} from '../../../data/apiCalls';
import { isDateInPast } from '../../../scripts/dateHelpers';
import CustomNoResultsOverlay from '../../shared/CustomNoResultsOverlay';
import { renderGridHeight } from './renderGridHeight';

const SlideLeft = props => <Slide {...props} direction='left' />;

const PendingSubAccountTable = () => {
	const [paginationModel, setPaginationModel] = useState({
		page: 0,
		pageSize: 10,
	});
	const [alertText, setAlertText] = useState({ title: '', description: '' });
	const [openSnackbar, setOpenSnackbar] = useState('');
	const [resendInviteModalOpen, setResendInviteModalOpen] = useState(false);
	const [subAccount, setSubAccount] = useState({});

	const handleModalOpen = () => setResendInviteModalOpen(true);
	const handleModalClose = () => setResendInviteModalOpen(false);

	const handleClick = params => {
		setSubAccount(params);
		handleModalOpen();
	};

	const { mutate, isLoading: isFetching } = useMutation({
		mutationFn: values => ResendAshlinkUserRegistration(values),
	});

	const { isLoading, isRefetching, ...accountData } = useQuery(
		['PendingSubAccount'],
		() => GetPendingSubAccountInfoFn(),
		{ staleTime: Infinity }
	);

	const pendingAccounts =
		accountData?.data?.data?.data?.pendingAshlinkUsers || [];

	const handleClose = reason => {
		if (reason?.type === 'click') {
			return;
		}
		setOpenSnackbar('');
	};

	const errorAlert = () => {
		const text = {
			title: 'Sorry, something went wrong.',
			description: `We were unable to process your request.`,
		};
		setAlertText(text);
		setOpenSnackbar('error');
	};

	const resendInvite = subAccountData => {
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		if (!emailRegex.test(subAccountData?.pendingAshlinkUserEmail)) {
			errorAlert();
			return;
		}
		mutate(subAccountData?.pendingAshlinkUserId, {
			onSuccess: () => {
				const email = subAccountData.pendingAshlinkUserEmail;
				const text = {
					title: 'Sub Account Creation Invite Sent',
					description: `An email has been sent to ${email} for them to create their account. The link will expire after 72 hours.`,
				};
				setAlertText(text);
				setOpenSnackbar('success');

				accountData.refetch();
			},
			onError: () => errorAlert(),
		});
	};

	useEffect(() => {
		if (resendInviteModalOpen && !isFetching) {
			handleModalClose();
		}
	}, [isFetching]);

	const columns = [
		{
			field: 'pendingAshlinkUserEmail',
			headerName: 'Email',
			minWidth: 270,
			flex: 2,
			valueFormatter: value => {
				if (!value) return '';
				return value;
			},
		},
		{
			field: 'dateSent',
			headerName: 'Date Sent',
			minWidth: 150,
			flex: 1,
			valueFormatter: value => {
				if (!value) return '';
				return new Date(value).toLocaleDateString();
			},
		},
		{
			field: 'expirationDate',
			headerName: 'Status',
			minWidth: 150,
			flex: 1,
			renderCell: params => {
				if (isDateInPast(params.value)) {
					return (
						<Chip
							label='Expired'
							variant='outlined'
							icon={<CircleIcon color='error' />}
							sx={{ textTransform: 'capitalize' }}
						/>
					);
				}
				return (
					<Chip
						label='Pending'
						variant='outlined'
						icon={<CircleOutlinedIcon />}
						sx={{ textTransform: 'capitalize' }}
					/>
				);
			},
		},
		{
			field: 'action',
			headerName: 'Action',
			minWidth: 145,

			renderCell: params => (
				<Button disableRipple onClick={() => handleClick(params.row)}>
					Resend Invite
				</Button>
			),
		},
	];

	return (
		<>
			<Box
				sx={{
					height: renderGridHeight(pendingAccounts, 691),
					width: '100%',
				}}
			>
				<DataGrid
					autoHeight={pendingAccounts?.length <= 10}
					pagination
					disableColumnMenu
					disableColumnFilter
					disableColumnResize
					disableColumnSelector
					disableDensitySelector
					hideFooterSelectedRowCount
					getRowId={row =>
						row.pendingAshlinkUserEmail + row.dateSent + row.status
					}
					rowHeight={58}
					pageSizeOptions={[10, 20, 50]}
					columns={columns}
					rows={pendingAccounts}
					loading={isLoading || isRefetching}
					paginationModel={paginationModel}
					onPaginationModelChange={setPaginationModel}
					initialState={{
						columns: { columnVisibilityModel: { id: false } },
						sorting: {
							sortModel: [{ field: 'dateSent', sort: 'desc' }],
						},
					}}
					slots={{
						noRowsOverlay: CustomNoResultsOverlay,
					}}
					slotProps={{
						noRowsOverlay: {
							customFailureVerbiage: 'No Pending Sub Accounts',
							customNoResultsVerbiage: 'No Pending Sub Accounts',
						},
					}}
					sx={{
						'& .MuiDataGrid-scrollbarFiller': {
							backgroundColor: 'rgb(0, 46, 110) !important', // eve theme is missing header background color when vertical scrollbar is present
						},
					}}
				/>
			</Box>

			<Dialog
				open={resendInviteModalOpen}
				onClose={handleModalClose}
				aria-labelledby='alert-dialog-title'
				aria-describedby='alert-dialog-description'
			>
				<DialogTitle id='alert-dialog-title'>Resend Invite</DialogTitle>
				<DialogContent>
					<Typography id='alert-dialog-description'>
						Are you sure you want to resend an invite to this sub
						account?
					</Typography>
					<Grid
						container
						mt={1}
						spacing={3}
						columns={{ xs: 4, sm: 12 }}
					>
						<Grid item xs={4} sm={3}>
							<Typography>
								<b>First Name</b>
							</Typography>
							<Typography>{subAccount?.firstName}</Typography>
						</Grid>
						<Grid item xs={4} sm={3}>
							<Typography>
								<b>Last Name</b>
							</Typography>
							<Typography>{subAccount?.lastName}</Typography>
						</Grid>
						<Grid item xs={4} sm={6}>
							<Typography>
								<b>Email</b>
							</Typography>
							<Typography>
								{subAccount?.pendingAshlinkUserEmail}
							</Typography>
						</Grid>
					</Grid>
				</DialogContent>
				<DialogActions sx={{ mb: 2 }}>
					<Button variant='outlined' onClick={handleModalClose}>
						Cancel
					</Button>
					<Button
						variant='contained'
						onClick={() => resendInvite(subAccount)}
						disabled={isFetching}
					>
						Resend Invite
						{isFetching && (
							<CircularProgress
								size={24}
								sx={{
									position: 'absolute',
									top: '50%',
									left: '50%',
									marginTop: '-12px',
									marginLeft: '-12px',
								}}
							/>
						)}
					</Button>
				</DialogActions>
			</Dialog>
			<Snackbar
				open={!!openSnackbar}
				autoHideDuration={10000}
				onClose={reason => handleClose(reason)}
				anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
				TransitionComponent={SlideLeft}
				sx={{
					boxShadow: 'rgba(0, 0, 0, 0.24) 0px 3px 8px',
				}}
			>
				<Alert severity={openSnackbar}>
					<Typography fontWeight={700}>{alertText.title}</Typography>
					<Typography>{alertText.description}</Typography>
				</Alert>
			</Snackbar>
		</>
	);
};

export default PendingSubAccountTable;
